export const getDataCoverageSortValue = (funds: FundProperty[]): number => {
    const coverageMap = getDataCoverageMap(funds);

    // Check if mixed coverage types - will apply penalty to those
    const coverageTypes = Array.from(coverageMap.entries()).filter(
        ([coverage, count]) => coverage !== "total" && count > 0
    );
    const hasMixedCoverage = coverageTypes.length > 1;

    // Using reducer to create coverage statistics for ranking
    const coverageStats = Array.from(coverageMap.entries())
        .filter(([coverage, count]) => coverage !== "total" && count > 0)
        .reduce(
            (acc, [coverage, count]) => {
                const ranking = RANKINGS[coverage as DataCoverageEnum] || 999;
                const points = POINTS[coverage as DataCoverageEnum] || 0;

                // Best point is used to rank funds with same ranking & worstRanking is used so funds with good ranking but also bad coverage are ranked after
                return {
                    bestRanking: Math.min(acc.bestRanking, ranking),
                    bestPoints:
                        ranking < acc.bestRanking || (ranking === acc.bestRanking && points > acc.bestPoints)
                            ? points
                            : acc.bestPoints,
                    worstRanking: Math.max(acc.worstRanking, ranking),
                    totalProperties: acc.totalProperties + count,
                };
            },
            {
                bestRanking: 999,
                bestPoints: -999,
                worstRanking: 0,
                totalProperties: 0,
            }
        );

    // Calculate final sort value - mixed coverage get penalties
    const baseSortValue = coverageStats.bestRanking * 1000 - coverageStats.bestPoints;

    const mixedPenalty = hasMixedCoverage ? (coverageStats.worstRanking - coverageStats.bestRanking) * 10 : 0;
    const sortValue = baseSortValue + mixedPenalty;

    return sortValue;
};

export const getPropertyDataCoverageSortValue = (propertyBuildingModels: PropertyBuildingModel[]): number => {
    const coverageMap = getPropertyDataCoverageMap(propertyBuildingModels);

    const total = coverageMap.get("total") || 0;

    if (total === 0) {
        return 8000; // High value to sort last
    }

    coverageMap.set("total", total);

    // Check if mixed coverage types
    const coverageTypes = Array.from(coverageMap.entries()).filter(
        ([coverage, count]) => coverage !== "total" && count > 0
    );
    const hasMixedCoverage = coverageTypes.length > 1;

    // Using reducer to create coverage statistics for ranking
    const coverageStats = Array.from(coverageMap.entries())
        .filter(([coverage, count]) => coverage !== "total" && count > 0)
        .reduce(
            (acc, [coverage]) => {
                const ranking = RANKINGS[coverage as DataCoverageEnum] || 999;
                const points = POINTS[coverage as DataCoverageEnum] || 0;

                return {
                    bestRanking: Math.min(acc.bestRanking, ranking),
                    bestPoints:
                        ranking < acc.bestRanking || (ranking === acc.bestRanking && points > acc.bestPoints)
                            ? points
                            : acc.bestPoints,
                    worstRanking: Math.max(acc.worstRanking, ranking),
                };
            },
            { bestRanking: 999, bestPoints: -999, worstRanking: 0 }
        );

    // Calculate final sort value - mixed coverage get penalties
    const baseSortValue = coverageStats.bestRanking * 1000 - coverageStats.bestPoints;
    const mixedPenalty = hasMixedCoverage ? (coverageStats.worstRanking - coverageStats.bestRanking) * 10 : 0;
    const sortValue = baseSortValue + mixedPenalty;

    return sortValue;
};

export const getDataCoverageMap = (funds: FundProperty[]) => {
    const map = new Map<DataCoverageEnum | "total", number>();

    const totalBuildings = funds.reduce((total, fund) => {
        fund.buildingModels.forEach(({ dataCoverage }) => {
            const identifier = dataCoverage.dataCoverage;
            if (identifier != null) {
                const currentIdentifierTotal = map.get(identifier) || 0;
                map.set(identifier, currentIdentifierTotal + 1);
            }
        });
        return total + fund.buildingModels.length;
    }, 0);

    map.set("total", totalBuildings);
    return map;
};

export const getPropertyDataCoverageMap = (propertyBuildingModels: PropertyBuildingModel[]) => {
    const map = new Map<DataCoverageEnum | "total", number>();

    const totalBuildings = propertyBuildingModels.reduce((total, { dataCoverage }) => {
        const identifier = dataCoverage?.dataCoverage;
        if (identifier != null) {
            const currentIdentifierTotal = map.get(identifier) || 0;
            map.set(identifier, currentIdentifierTotal + 1);
        }
        return total + 1;
    }, 0);

    map.set("total", totalBuildings);
    return map;
};

type DataCoverageEnum =
    | "equipment_and_utility"
    | "equipment"
    | "utility"
    | "no_equipment_and_no_utility"
    | "equipment_warning"
    | "utility_warning"
    | "equipment_and_utility_warning"
    | "error"
    | "processing";

const RANKINGS: Record<DataCoverageEnum, number> = {
    equipment_and_utility: 1,
    equipment_and_utility_warning: 2,
    equipment: 3,
    equipment_warning: 4,
    utility: 5,
    utility_warning: 6,
    no_equipment_and_no_utility: 7,
    error: 8,
    processing: 9,
};

const POINTS: Record<DataCoverageEnum, number> = {
    equipment_and_utility: 4,
    equipment_and_utility_warning: 4,
    equipment: 3,
    equipment_warning: 3,
    utility: 3,
    utility_warning: 3,
    no_equipment_and_no_utility: 2,
    error: -4,
    processing: 0,
};
